import React, {useState} from 'react';
import {
  EditView,
  SimpleForm,
  TextField,
  TextInput,
  SelectInput,
  required,
  //PasswordInput,
  useNotify, useRefresh,
  //useRedirect,
  Loading,
  useGetList,
  EditContextProvider,
  useEditController,
  useRecordContext,
  usePermissions,
  useTranslate,
  useDataProvider
} from 'react-admin';
import {useWatch} from 'react-hook-form';
import {Button, Box, Typography} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import log from 'loglevel';
import {queryParamObject} from "../util/paramUtil";
import {Breadcrumbs} from "../breadcrumbs";
import {UnEditActions} from "../components/EditActions";
import {UnEditToolbar, DetailTitle} from "../components/InputFields";
import {Activity, isAuthorized} from "../auth/authorization";

import {
  validateFirstName,
  validateLastName,
  validateEmail,
  //validatePassword
} from "./UserValidate";
import {useLocation, useParams} from "react-router-dom";

/**
 * TODO: toLowerCase of email not working
 * parse - prepare input value for output
 * format - prepare value read before display - format={v => v.toLowerCase()}
 * Associated reference no longer appears to be available.
 */

//const CommentField = ({value}) => value;

// const UserEditToolbar = props => {
//   return (
//     <Toolbar {...props}>
//       <SaveButton/>
//     </Toolbar>
//   )
// };
// const UserEditActions = ({data, resource}) => {
//   if (!data) return null;
//   return (
//     <TopToolbar>
//       <UnShowButtonWithName record={data} resource={resource}/>
//     </TopToolbar>
//   );
// };


//const UserTitle = ({record}) => <span>User: {record ? `${record.name}` : ''}</span>;

// Pass name to 2nd breadcrumb in show/detail page
//const redirect = (basePath, id, {name}) => `${basePath}/${id}/show?${queryParamString({name})}`;

// Time formats to milliseconds: https://github.com/vercel/ms
const expirations = [
  {id: '1m', name: '1 minute'},
  {id: '2m', name: '2 minutes'},  // Testing 2 * 60000
  {id: '1h', name: '1 hour'},
  {id: '1d', name: '1 day'},
  {id: '7d', name: '7 days'},
  {id: '30d', name: '30 days'},
  {id: '120d', name: '120 days'},
  {id: '180d', name: '180 days'},
];

/**
 * MFA Management Section Component
 * Shows MFA controls based on user permissions
 * - Superadmin: Can edit MFA requirement and reset MFA
 * - Admin: Can only view MFA requirement (read-only)
 */
const MFAManagementSection = () => {
  const {permissions} = usePermissions();
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  // Convert permissions string to array (e.g. "superadmin,un_admin" -> ["superadmin", "un_admin"])
  const rolesArray = permissions ? permissions.split(',') : [];

  // Check if user can manage MFA settings
  const canManageMFA = isAuthorized(rolesArray, Activity.USER_MFA_MANAGE);

  // MFA requirement choices with translations
  const mfaRequirementChoices = [
    {id: false, name: translate('resources.users.mfa.choice_require')},      // mfa_disabled = false means MFA is required
    {id: true, name: translate('resources.users.mfa.choice_disable')},       // mfa_disabled = true means MFA is disabled (bypassed)
  ];

  return (
    <>
      <SelectInput
        source="mfa_disabled"
        label={translate('resources.users.fields.mfa_disabled')}
        choices={mfaRequirementChoices}
        defaultValue={false}
        validate={required()}
        disabled={!canManageMFA}
        fullWidth
        helperText={
          canManageMFA
            ? translate('resources.users.mfa.helper_superadmin')
            : translate('resources.users.mfa.helper_readonly')
        }
      />
      {canManageMFA && <ResetMFAButton />}
    </>
  );
};

/**
 * Reset MFA Button Component
 * Calls the MFA reset endpoint to clear user's MFA settings
 * Only visible to superadmin
 * Uses dataProvider.update() with 'mfa' resource which is mapped to admin/users/{user_id}/mfa/reset
 */
const ResetMFAButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);

  // Watch form values to detect when dropdown changes (real-time)
  // useWatch returns the current value from the form, even before save
  const currentMfaDisabled = useWatch({name: 'mfa_disabled', defaultValue: record.mfa_disabled});

  if (!record) return null;

  const handleResetMFA = async () => {
    if (!window.confirm(translate('resources.users.mfa.reset_confirm_message'))) {
      return;
    }

    setLoading(true);
    try {
      log.debug(`{ResetMFAButton} Resetting MFA for user: ${record.id}`);

      // Call dataProvider.update() with 'mfa' resource
      // The mapper.js will convert this to: PUT /admin/users/{user_id}/mfa/reset
      await dataProvider.update('mfa', {
        id: record.id,
        data: {},  // No data needed for reset
        previousData: record
      });

      notify(translate('resources.users.mfa.reset_success'), { type: 'success' });
      refresh();
    } catch (error) {
      log.error(`{ResetMFAButton} Error resetting MFA:`, error);
      notify(translate('resources.users.mfa.reset_error', { error: error.message || 'Unknown error' }), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getHelperText = () => {
    if (currentMfaDisabled) {
      return translate('resources.users.mfa.disabled_by_admin');
    }
    if (!record.mfa_registered) {
      return translate('resources.users.mfa.not_registered');
    }
    return null;
  };

  // Disable button if:
  // - Currently loading
  // - MFA is disabled (either saved in record OR currently selected in dropdown)
  // - User hasn't registered MFA yet
  const isDisabled = loading || currentMfaDisabled || !record.mfa_registered;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Button
        variant="outlined"
        color="warning"
        startIcon={<RestartAltIcon />}
        onClick={handleResetMFA}
        disabled={isDisabled}
      >
        {loading ? translate('resources.users.mfa.resetting') : translate('resources.users.mfa.reset_button')}
      </Button>
      {getHelperText() && (
        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
          {getHelperText()}
        </Typography>
      )}
    </Box>
  );
};

export const UserEdit = () => {
  const location = useLocation();
  const {id} = useParams();
  // const notify = useNotify();
  // const refresh = useRefresh();
  // const redirect = useRedirect();
  const {name, type} = queryParamObject(location);
  log.debug(`{UserEdit} id: ${id}, name: ${name}, type: ${type}`);

  // Note undoable must be set to false in Edit for data arg to be populated
  // const onSuccess = ({data}) => {
  //   log.debug(`{UserEdit.onSuccess} data:`, data);
  //   const {user} = data;
  //   const name = `${user.first_name} ${user.last_name}`;
  //   // https://marmelab.com/react-admin/Actions.html#handling-side-effects-in-usedataprovider
  //   notify(`notification.updated_one`, {type: 'info'});
  //   // redirect to the result of a function
  //   // redirect((redirectTo, basePath, is, data) => ...)
  //   const redirectUrl = `/users/${user._id}/show?${queryParamString({name})}`;
  //   log.debug(`{UserEdit.onSuccess} redirectUrl:`, redirectUrl);
  //   redirect(redirectUrl);
  //   refresh();
  // };

  // transform the form data before calling dataProvider.update()
  const transform = data => {
    //log.debug(`{UserEdit.transform} data:`, data);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {hashedPassword, ...rest} = data;
    //log.debug(`{UserEdit.transform} transformed data:`, rest);
    return rest;
  };

  const editContext = useEditController({redirect: 'show', transform});

  const {
    isLoading: isRepresentativesLoading,
    data: representatives
  } = useGetList('representatives', {
    pagination: {page: 1},
    sort: {field: 'name', order: 'ASC'}
  });
  if (editContext.isLoading) return <Loading />;
  if (isRepresentativesLoading) return <Loading />;
  const representativesList = representatives.map(r => ({id: r.uname, name: r.name}));
  log.debug(`{UserEdit} representativesList:`, representativesList);
  return (
    <EditContextProvider value={editContext}>
      <Breadcrumbs/>
      <EditView
        title={<DetailTitle />}
        actions={<UnEditActions />}
        // transform={transform}
        undoable={false}  // Needed for onSuccess to receive data
      >
        <SimpleForm  toolbar={<UnEditToolbar/>}>
          <TextInput source="first_name" validate={validateFirstName} fullWidth />
          <TextInput source="last_name" validate={validateLastName} fullWidth />
          <TextInput source="email" parse={v => v.toLowerCase()} validate={validateEmail} fullWidth />

          {/* MFA Management Section */}
          <MFAManagementSection />

          <TextField source="user_name" label='User Name' />

          {/*<PasswordInput source="password" validate={validatePassword} fullWidth />*/}

          {/*<ReferenceInput label="Application" source="rep_uname" reference="representatives"*/}
          {/*                sort={{ field: 'name', order: 'ASC' }} allowEmpty>*/}
          {/*  <SelectInput optionText="name" optionValue="uname"/>*/}
          {/*</ReferenceInput>*/}
          {/*<SelectInput label="Application (rep_uname) - deprecated" source="rep_uname" choices={representativesList} fullWidth allowEmpty/>*/}

          <SelectInput source='expiration' choices={expirations} validate={required()}/>
          <TextField source="rep_uname" />
          <TextField label="ID" source="id" />
          {/*<CommentField value='Ignore message: Associated reference no longer appears to be available.' />*/}
        </SimpleForm>
      </EditView>
    </EditContextProvider>
  );
}




export default UserEdit;